// Client-side dust balance manager with localStorage cache and transaction log

import { DustTransactionType } from '../multiplayer/protocol';

export interface DustTransaction {
  type: DustTransactionType;
  amount: number;
  balance: number;
  timestamp: number;
}

const STORAGE_KEY_DUST = 'wizard_duel_dust_balance';
const STORAGE_KEY_TRANSACTIONS = 'wizard_duel_dust_transactions';
const MAX_TRANSACTIONS = 20;

export class DustManager {
  private balance: number = 0;
  private transactions: DustTransaction[] = [];
  private listeners: Set<(balance: number) => void> = new Set();

  constructor() {
    this.balance = this.loadBalance();
    this.transactions = this.loadTransactions();
  }

  private loadBalance(): number {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_DUST);
      if (raw) return parseInt(raw, 10) || 0;
    } catch { /* ignore */ }
    return 0;
  }

  private saveBalance(): void {
    try {
      localStorage.setItem(STORAGE_KEY_DUST, String(this.balance));
    } catch { /* ignore */ }
  }

  private loadTransactions(): DustTransaction[] {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_TRANSACTIONS);
      if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    return [];
  }

  private saveTransactions(): void {
    try {
      localStorage.setItem(STORAGE_KEY_TRANSACTIONS, JSON.stringify(this.transactions));
    } catch { /* ignore */ }
  }

  /** Sync balance from server (authoritative) */
  setBalance(dust: number): void {
    this.balance = dust;
    this.saveBalance();
    this.notifyListeners();
  }

  /** Record a dust transaction from the server */
  recordTransaction(type: DustTransactionType, amount: number, newBalance: number): void {
    this.balance = newBalance;
    this.saveBalance();

    this.transactions.push({
      type,
      amount,
      balance: newBalance,
      timestamp: Date.now(),
    });

    // Keep only the last N transactions
    while (this.transactions.length > MAX_TRANSACTIONS) {
      this.transactions.shift();
    }
    this.saveTransactions();
    this.notifyListeners();
  }

  getBalance(): number {
    return this.balance;
  }

  getTransactions(): DustTransaction[] {
    return [...this.transactions];
  }

  /** Listen for balance changes */
  onChange(listener: (balance: number) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notifyListeners(): void {
    for (const listener of this.listeners) {
      listener(this.balance);
    }
  }

  /** Format dust amount with rolling animation data */
  static formatDust(amount: number): string {
    return `${amount.toLocaleString()} Dust`;
  }

  /** Get a human-readable label for a transaction type */
  static getTransactionLabel(type: DustTransactionType): string {
    const labels: Record<DustTransactionType, string> = {
      match_win: 'Match Victory',
      match_loss: 'Match Participation',
      achievement: 'Achievement Unlocked',
      tournament_win: 'Tournament Victory',
      daily_login: 'Daily Login Bonus',
      bet_win: 'Bet Won',
      bet_loss: 'Bet Lost',
      shop_purchase: 'Shop Purchase',
      bet_refund: 'Bet Refunded',
    };
    return labels[type] || type;
  }
}
